import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import WPHeading from "../../../../../packages/WPHeading";
import WPTypography from "../../../../../packages/WPTypography/WPTypography";

import UserDefaultLicenseCard from "../../../../Header/UserProfile/LicenseDetailsDialog/UserLicenseDetails/UserLicenseCard/UserDefaultLicenseCard";
import LicenseDetailsCard from "./LicenseDetailsCard";

import {
  HIDDEN_LICENSE_IDS,
  LICENSE_STATUS,
} from "../../../../../constants/LicenseManagement.constants";

const LicenseDetailsSection = ({
  userData,
  onLicenseAction,
  onLicenseUpdate,
}) => {
  const { t } = useTranslation();
  const { licenses = [] } = userData || {};
  const { expiringSoon, expired, active, terminated } = LICENSE_STATUS;

  const activeOrExpiringLicenses = useMemo(() => {
    const filteredLicenses = licenses.filter(
      ({ licenseStatus, id }) =>
        (licenseStatus === active || licenseStatus === expiringSoon) &&
        !HIDDEN_LICENSE_IDS.includes(id),
    );

    const activeLicenses = filteredLicenses.filter(
      ({ licenseStatus }) => licenseStatus === active,
    );
    const expiringSoonLicenses = filteredLicenses.filter(
      ({ licenseStatus }) => licenseStatus === expiringSoon,
    );

    return [...activeLicenses, ...expiringSoonLicenses];
  }, [licenses, active, expiringSoon]);

  const expiredLicenses = useMemo(
    () => licenses.filter((license) => license.licenseStatus === expired),
    [licenses, expired],
  );

  const terminatedLicenses = useMemo(
    () => licenses.filter((license) => license.licenseStatus === terminated),
    [licenses, terminated],
  );

  return (
    <div className="license-details-section-content">
      <WPTypography
        text={t("licenseManagement.licensePermissionTabDesc")}
        className="license-permission-text"
        id="license_permission_text"
      />
      {activeOrExpiringLicenses.length > 0 && (
        <>
          <WPHeading
            id="license_active_header"
            text={t("licenseManagement.licenseCardActiveExpiry")}
            secondary
            size="small"
            className="license-permission-header license-permission-header--muted"
          />
          <div className="license-permission-content">
            {activeOrExpiringLicenses.map((license) => (
              <LicenseDetailsCard
                key={license.id}
                license={license}
                onLicenseAction={onLicenseAction}
                userData={userData}
                onLicenseUpdate={onLicenseUpdate}
              />
            ))}
          </div>
        </>
      )}
      {expiredLicenses.length > 0 && (
        <>
          <WPHeading
            id="license_expired_header"
            text={t("licenseManagement.filterExpired")}
            secondary
            size="small"
            className="license-permission-header license-permission-header--muted"
          />
          <div className="license-permission-content">
            {expiredLicenses.map((license) => (
              <LicenseDetailsCard
                key={license.id}
                license={license}
                onLicenseAction={onLicenseAction}
                userData={userData}
                onLicenseUpdate={onLicenseUpdate}
              />
            ))}
          </div>
        </>
      )}
      {terminatedLicenses.length > 0 && (
        <>
          <WPHeading
            id="license_terminated_header"
            text={t("licenseManagement.filterTerminated")}
            secondary
            size="small"
            className="license-permission-header license-permission-header--muted"
          />
          <div className="license-permission-content">
            {terminatedLicenses.map((license) => (
              <LicenseDetailsCard
                key={license.id}
                license={license}
                onLicenseAction={onLicenseAction}
                userData={userData}
                onLicenseUpdate={onLicenseUpdate}
              />
            ))}
          </div>
        </>
      )}
      {activeOrExpiringLicenses.length === 0 &&
        expiredLicenses.length === 0 &&
        terminatedLicenses.length === 0 && (
          <UserDefaultLicenseCard description="licenseManagement.defaultLicensePermission" />
        )}
    </div>
  );
};

export default LicenseDetailsSection;
